import { Injectable, inject } from '@angular/core';
import { combineLatest, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { Composer, InvoiceStatus } from '../../models/db-models';
import { AuthService } from '../auth/auth.service';
import { DbService } from './db.service';
import { NotificationService } from './notification.service';
import { StorageKeys, StorageService } from './storage.service';
import { Payee } from '../../models/external-system.model';
import { ArtistService } from './artist.service';

@Injectable({
  providedIn: 'root'
})
export class PayeeService {
  private storage = inject(StorageService);
  private auth = inject(AuthService);
  private db = inject(DbService);
  private notificationService = inject(NotificationService);
  private artistService = inject(ArtistService);
  private _signedInPayee$: Subject<Payee> = new ReplaySubject(1);
  private _selectedPayeeId$: Subject<string> = new ReplaySubject(1);
  private _selectedComposer$: Subject<Composer> = new ReplaySubject(1);
  private payeeSubscription: Subscription;

  constructor() {
    this.init();
  }

  get signedInPayee$(): Observable<Payee> {
    return this._signedInPayee$;
  }

  get selectedPayeeId$(): Observable<string> {
    return this._selectedPayeeId$;
  }

  get selectedComposer$(): Observable<Composer> {
    return this._selectedComposer$;
  }

  async init() {
    combineLatest([this.auth.user$, this.auth.customClaims$])
      .subscribe(async ([signedInUser, customClaims]) => {
        if (!signedInUser || !customClaims) {
          return;
        }

        let userId = signedInUser?.uid;
        if (this.auth.hasAnyCustomClaims(customClaims, ['manager', 'finance'])) {
          userId = await this.storage.get(StorageKeys.selectedPayeeId);
        } else {
          this.auth.updateSignedInTime(userId);
          this.notificationService.requestPermission(userId);
        }
        
        const isManager = this.auth.hasAnyCustomClaims(customClaims, ['manager', 'finance']);
        const selectedPayeeId = await this.storage.get(StorageKeys.selectedPayeeId);

        if (!isManager && !selectedPayeeId) {
          this.fetchPayee(userId);
        } else {
          this.fetchPayee(selectedPayeeId);
        }
      });
  }

  async fetchPayee(id: string) {
    if (id) {
      this.auth.requestExternalAccess(id);
      this.payeeSubscription?.unsubscribe();
      this.payeeSubscription = this.db.getPayee(id)
      .subscribe(async (payee) => {
        if (payee) {
          this._signedInPayee$.next(payee);
          this.selectComposer(payee);
        }
      });

      this.artistService.fetchArtist(id, false);

    } else {
      this._signedInPayee$.next(null);
    }
  }

  async selectPayeeId(id: string) {
    this._selectedPayeeId$.next(id);
    await this.storage.set(StorageKeys.selectedPayeeId, id);
    await this.artistService.clearSelectedArtistId();
    this.fetchPayee(id);
  }

  async selectComposer(composer: Payee | Composer) {
    this._selectedComposer$.next(composer);
  }

  getTranslatedStatusAdmin(invoiceStatus: string) {
    switch (invoiceStatus) {
      case InvoiceStatus.OPEN:
        return 'Em aberto';
      case InvoiceStatus.REQUESTED:
        return 'Solicitado';
      case InvoiceStatus.SCHEDULED:
        return 'Agendado';
      case InvoiceStatus.PAID:
        return 'Pago';
      case InvoiceStatus.ADVANCE:
        return 'ADIANTAMENTO';
    }
    return 'indisponível';
  }

  getColorByStatus(invoiceStatus: string) {
    switch (invoiceStatus) {
      case InvoiceStatus.OPEN:
        return 'success';
      case InvoiceStatus.ADVANCE:
        return 'medium';
      case InvoiceStatus.REQUESTED:
      case InvoiceStatus.SCHEDULED:
        return 'pending';
      case InvoiceStatus.PAID:
        return 'danger';
    }
    return 'primary';
  }

  getTranslatedStatus(invoiceStatus: string, total: number = 0) {
    switch (invoiceStatus) {
      case InvoiceStatus.OPEN:
        return 'disponível';
      case InvoiceStatus.REQUESTED:
      case InvoiceStatus.SCHEDULED:
        return 'em andamento';
      case InvoiceStatus.PAID:
        return 'pago';
      case InvoiceStatus.ADVANCE:
        return total >= 0 ? 'adiantamento' : 'crédito';
    }
    return 'indisponível';
  }

}
